import { useCallback, useEffect, useState } from 'react'
import { prefsStore } from '@/app/preferences/prefsStore'
import type { ListViewKind } from './viewCandidates'
import { sanitizeAggregatesLayout, type AggregatesLayout } from './aggregatesLayout'

export interface ViewPrefs {
  view: ListViewKind
  kanbanField: string
  calendarField: string
  /** @deprecated legacy single-field grouping — migrated to `groupByFields`. */
  groupByField: string
  /** Ordered multi-field grouping (selection order = grouping order). */
  groupByFields: string[]
  /** Configured `field:op` pairs for the list-footer Σ aggregates row (list view only). */
  aggregates: string[]
  /** How selected aggregates render in the footer. */
  aggregatesLayout: AggregatesLayout
  /** Σ field order; empty = column LTR. */
  aggregatesFieldOrder: string[]
}

export type ViewPrefsDefaultView = 'list' | 'kanban' | 'tree'

export interface UseViewPrefsOpts {
  hierarchical?: boolean
  /** Used when no prefs are stored for the scope (e.g. hierarchical → `tree`). */
  defaultView?: ViewPrefsDefaultView
}

const DEFAULT_PREFS: ViewPrefs = {
  view: 'list',
  kanbanField: '',
  calendarField: '',
  groupByField: '',
  groupByFields: [],
  aggregates: [],
  aggregatesLayout: 'horizontal',
  aggregatesFieldOrder: [],
}

const storageKeyFor = (scope: string) => `list_view_prefs:${scope}`

export function migrateViewKind(
  view: string | undefined,
  hierarchical: boolean,
): 'list' | 'kanban' | 'tree' {
  if (view === 'kanban') return 'kanban'
  if (view === 'tree') return 'tree'
  if (view === 'calendar') return hierarchical ? 'tree' : 'list'
  return 'list'
}

export function normalizeViewPrefs(
  prefs: Partial<ViewPrefs>,
  opts?: UseViewPrefsOpts,
): ViewPrefs {
  const merged: ViewPrefs = { ...DEFAULT_PREFS, ...prefs }
  merged.view = migrateViewKind(merged.view, opts?.hierarchical ?? false)
  if (!Array.isArray(prefs.groupByFields) && prefs.groupByField) {
    merged.groupByFields = [prefs.groupByField]
  }
  merged.aggregatesLayout = sanitizeAggregatesLayout(merged.aggregatesLayout)
  merged.aggregatesFieldOrder = Array.isArray(prefs.aggregatesFieldOrder)
    ? prefs.aggregatesFieldOrder.filter((x): x is string => typeof x === 'string')
    : []
  return merged
}

function emptyPrefs(opts?: UseViewPrefsOpts): ViewPrefs {
  const defaultView = opts?.defaultView ?? DEFAULT_PREFS.view
  return normalizeViewPrefs({ ...DEFAULT_PREFS, view: defaultView }, opts)
}

function loadPrefs(scope: string, opts?: UseViewPrefsOpts): ViewPrefs {
  try {
    const raw = prefsStore.getItem(storageKeyFor(scope))
    if (!raw) return emptyPrefs(opts)
    const parsed = JSON.parse(raw) as Partial<ViewPrefs>
    return normalizeViewPrefs(parsed, opts)
  } catch {
    return emptyPrefs(opts)
  }
}

export function useViewPrefs(scope: string, opts?: UseViewPrefsOpts) {
  const [prefs, setPrefs] = useState<ViewPrefs>(() => loadPrefs(scope, opts))

  useEffect(() => {
    setPrefs(loadPrefs(scope, opts))
  }, [scope, opts?.hierarchical, opts?.defaultView])

  const update = useCallback(
    (patch: Partial<ViewPrefs>) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch }
        try {
          prefsStore.setItem(storageKeyFor(scope), JSON.stringify(next))
        } catch {
          /* private browsing */
        }
        return next
      })
    },
    [scope],
  )

  return { prefs, update }
}
